import { useEffect, useRef, useState, useCallback } from "react";
import worldMapDots from "../../assets/img/world-map-dots.png";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const CITIES = [
  { x: 27, y: 22, name: "New York" },
  { x: 20, y: 32, name: "Houston" },
  { x: 30, y: 58, name: "São Paulo" },
  { x: 45, y: 15, name: "London" },
  { x: 46, y: 18, name: "Rotterdam" },
  { x: 46, y: 42, name: "Lagos" },
  { x: 53, y: 48, name: "Nairobi" },
  { x: 57, y: 28, name: "Dubai" },
  { x: 63, y: 33, name: "Mumbai" },
  { x: 65, y: 40, name: "Chennai" },
  { x: 75, y: 20, name: "Beijing" },
  { x: 77, y: 28, name: "Shanghai" },
  { x: 78, y: 44, name: "Singapore" },
  { x: 83, y: 22, name: "Tokyo" },
  { x: 84, y: 65, name: "Sydney" },
];

const ROUTES = [
  { from: { x: 27, y: 22 }, to: { x: 63, y: 33 } }, // USA → India
  { from: { x: 57, y: 28 }, to: { x: 46, y: 18 } }, // UAE → Germany
  { from: { x: 84, y: 65 }, to: { x: 27, y: 22 } }, // Australia → USA
];

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const imgRectRef = useRef<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const getImgRect = useCallback(() => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return null;
    const cRect = container.getBoundingClientRect();
    const natW = img.naturalWidth || 1920;
    const natH = img.naturalHeight || 1080;
    const scale = Math.min(cRect.width / natW, cRect.height / natH);
    const rW = natW * scale;
    const rH = natH * scale;
    return {
      left: (cRect.width - rW) / 2,
      top: (cRect.height - rH) / 2,
      width: rW,
      height: rH,
    };
  }, []);

  const toPixel = useCallback(
    (
      pt: { x: number; y: number },
      rect: { left: number; top: number; width: number; height: number },
    ) => ({
      px: rect.left + (pt.x / 100) * rect.width,
      py: rect.top + (pt.y / 100) * rect.height,
    }),
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const routeState = ROUTES.map((_, i) => ({
      t: Math.random(),
      speed: 0.003 + i * 0.0005,
    }));

    let time = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      imgRectRef.current = getImgRect();
    };

    const quadBezier = (t: number, p0: number, p1: number, p2: number) =>
      (1 - t) ** 2 * p0 + 2 * (1 - t) * t * p1 + t * t * p2;

    const draw = () => {
      const container = containerRef.current;
      if (!container) {
        animRef.current = requestAnimationFrame(draw);
        return;
      }
      const cRect = container.getBoundingClientRect();
      ctx.clearRect(0, 0, cRect.width, cRect.height);
      time += 0.016;

      const ir = imgRectRef.current;
      if (!ir) {
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      // Draw city markers
      CITIES.forEach((city, i) => {
        const { px, py } = toPixel(city, ir);
        const pulse = Math.sin(time * (1.5 + (i % 4) * 0.3)) * 0.5 + 0.5;

        // Outer pulse ring
        const ringR = 4 + pulse * 8;
        ctx.beginPath();
        ctx.arc(px, py, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(220, 90%, 56%, ${(1 - pulse) * 0.3})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Soft glow
        const grad = ctx.createRadialGradient(px, py, 0, px, py, 8);
        grad.addColorStop(0, `hsla(220, 90%, 60%, ${0.25 + pulse * 0.15})`);
        grad.addColorStop(1, "hsla(220, 90%, 60%, 0)");
        ctx.beginPath();
        ctx.arc(px, py, 8, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(220, 90%, 65%, ${0.7 + pulse * 0.3})`;
        ctx.fill();

        // Bright center
        ctx.beginPath();
        ctx.arc(px, py, 1, 0, Math.PI * 2);
        ctx.fillStyle = "hsla(220, 90%, 92%, 0.95)";
        ctx.fill();
      });

      // Draw trade routes with glowing orbs + fading trails
      ROUTES.forEach((route, i) => {
        const rs = routeState[i];
        rs.t = (rs.t + rs.speed) % 1;

        const { px: fx, py: fy } = toPixel(route.from, ir);
        const { px: tx, py: ty } = toPixel(route.to, ir);
        const cx = (fx + tx) / 2;
        const dist = Math.sqrt((tx - fx) ** 2 + (ty - fy) ** 2);
        const cy = Math.min(fy, ty) - dist * 0.25 - 10;

        // Dashed arc path
        ctx.beginPath();
        ctx.setLineDash([4, 6]);
        ctx.moveTo(fx, fy);
        ctx.quadraticCurveTo(cx, cy, tx, ty);
        ctx.strokeStyle = "hsla(220, 90%, 56%, 0.15)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);

        // Fading trail (12 segments behind the orb)
        const trailLength = 12;
        for (let s = trailLength; s >= 0; s--) {
          const tt = (rs.t - s * 0.012 + 1) % 1;
          const tpx = quadBezier(tt, fx, cx, tx);
          const tpy = quadBezier(tt, fy, cy, ty);
          const alpha = (1 - s / trailLength) * 0.5;
          const radius = 3 - (s / trailLength) * 2;

          if (radius > 0.5) {
            ctx.beginPath();
            ctx.arc(tpx, tpy, radius, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(220, 90%, 65%, ${alpha})`;
            ctx.fill();
          }
        }

        // Main glowing orb
        const orbX = quadBezier(rs.t, fx, cx, tx);
        const orbY = quadBezier(rs.t, fy, cy, ty);

        // Outer glow
        const orbGrad = ctx.createRadialGradient(orbX, orbY, 0, orbX, orbY, 14);
        orbGrad.addColorStop(0, "hsla(220, 90%, 70%, 0.5)");
        orbGrad.addColorStop(0.4, "hsla(220, 90%, 60%, 0.2)");
        orbGrad.addColorStop(1, "hsla(220, 90%, 56%, 0)");
        ctx.beginPath();
        ctx.arc(orbX, orbY, 14, 0, Math.PI * 2);
        ctx.fillStyle = orbGrad;
        ctx.fill();

        // Bright orb core
        ctx.beginPath();
        ctx.arc(orbX, orbY, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = "hsla(220, 90%, 75%, 0.85)";
        ctx.fill();

        // White hot center
        ctx.beginPath();
        ctx.arc(orbX, orbY, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = "hsla(220, 90%, 95%, 0.95)";
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    const onImgLoad = () => {
      resize();
      draw();
    };

    const img = imgRef.current;
    if (img?.complete) {
      onImgLoad();
    } else if (img) {
      img.addEventListener("load", onImgLoad);
    }
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      img?.removeEventListener("load", onImgLoad);
      cancelAnimationFrame(animRef.current);
    };
  }, [getImgRect, toPixel]);

  return (
    <section className="relative pt-16 overflow-hidden">
      <div ref={containerRef} className="relative h-[440px] md:h-[520px]">
        <img
          ref={imgRef}
          src={worldMapDots}
          alt=""
          className={`absolute inset-0 w-full h-full object-contain opacity-0 transition-opacity duration-1000 ${isVisible ? "opacity-40" : ""}`}
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
        />

        {/* Floating stat cards */}
        <div className="absolute top-[16%] left-[4%] md:left-[8%] bg-background/90 backdrop-blur-sm border border-border rounded-xl px-5 py-3.5 shadow-lg animate-float z-10">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse-dot" />
            India → Middle East
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            $4.2M
          </p>
          <p className="text-sm text-muted-foreground">
            petrochemicals sourced
          </p>
        </div>

        <div className="absolute top-[10%] right-[4%] md:right-[10%] bg-background/90 backdrop-blur-sm border border-border rounded-xl px-5 py-3.5 shadow-lg animate-float-delayed z-10">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse-dot" />
            Brazil → Southeast Asia
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            12,000 MT
          </p>
          <p className="text-sm text-muted-foreground">
            agro commodities shipped
          </p>
        </div>

        <div className="absolute bottom-[16%] left-[30%] md:left-[40%] bg-background/90 backdrop-blur-sm border border-border rounded-xl px-5 py-3.5 shadow-lg animate-float-delayed-2 hidden md:block z-10">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse-dot" />
            Africa → China
          </div>
          <p className="text-xl font-display font-bold text-foreground">
            8,500 MT
          </p>
          <p className="text-sm text-muted-foreground">minerals procured</p>
        </div>
      </div>

      <div className="container px-4 -mt-12 md:-mt-4 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <h1 className="text-4xl md:text-5xl lg:text-[3.75rem] font-display font-bold text-foreground leading-[1.08] max-w-2xl tracking-tight">
            Global procurement
            <br />
            is stuck in the past.
            <br />
            <span className="text-gradient">We're building its future.</span>
          </h1>
          <div className="max-w-md lg:pb-2">
            <p className="text-base font-medium text-muted-foreground leading-relaxed mb-6">
              Taram is an AI-powered procurement, finance & logistics company.
              We train our models on historic trade data to source, negotiate,
              quality-check, finance, and ship commodities across global supply
              chains.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={"/signup"}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#offices"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-lg border border-border text-foreground hover:bg-secondary transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Speak to Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
